import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ThirdPartyContactWithStatus } from './types';

const RESTORE_BATCH_SIZE = 50;

export interface UseRestoreContactsParams {
  thirdPartyId: string;
  selectedContacts: ThirdPartyContactWithStatus[];
  restoreContacts: (input: {
    ThirdPartyId: string;
    ContactIds: string[];
  }) => Promise<unknown>;
  addNotification: (notification: {
    type: 'success' | 'error';
    content: string;
  }) => void;
  refetchContacts: () => Promise<unknown>;
  onSuccess?: () => void;
}

export interface UseRestoreContactsResult {
  restorableContacts: ThirdPartyContactWithStatus[];
  handleRestoreAccess: () => Promise<void>;
}

export const filterRestorableContacts = (
  contacts: ThirdPartyContactWithStatus[]
): ThirdPartyContactWithStatus[] => {
  return contacts.filter((contact) => contact.status === 'revoked');
};

export const useRestoreContacts = ({
  thirdPartyId,
  selectedContacts,
  restoreContacts,
  addNotification,
  refetchContacts,
  onSuccess,
}: UseRestoreContactsParams): UseRestoreContactsResult => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'third_party.contacts',
  });
  const restorableContacts = useMemo(
    () => filterRestorableContacts(selectedContacts),
    [selectedContacts]
  );

  const handleRestoreAccess = useCallback(async () => {
    if (!restorableContacts.length) {
      return;
    }

    const contactIds = restorableContacts.map((contact) => contact.Id);
    let restoredCount = 0;

    try {
      for (
        let offset = 0;
        offset < contactIds.length;
        offset += RESTORE_BATCH_SIZE
      ) {
        const batch = contactIds.slice(offset, offset + RESTORE_BATCH_SIZE);
        await restoreContacts({
          ThirdPartyId: thirdPartyId,
          ContactIds: batch,
        });
        restoredCount += batch.length;
      }
    } catch (_error) {
      if (restoredCount > 0) {
        onSuccess?.();
      }

      try {
        await refetchContacts();

        addNotification({
          type: 'error',
          content:
            restoredCount > 0
              ? t('restore_access_partial_error', { count: restoredCount })
              : t('restore_access_uncertain_error'),
        });
      } catch (_refreshError) {
        addNotification({
          type: 'error',
          content: t('restore_access_failure_refresh_error'),
        });
      }

      return;
    }

    addNotification({
      type: 'success',
      content:
        restorableContacts.length === 1
          ? t('restore_access_success')
          : t('restore_access_success_multiple', {
              count: restorableContacts.length,
            }),
    });
    onSuccess?.();

    try {
      await refetchContacts();
    } catch (_error) {
      addNotification({
        type: 'error',
        content: t('restore_access_refresh_error'),
      });
    }
  }, [
    addNotification,
    onSuccess,
    refetchContacts,
    restorableContacts,
    restoreContacts,
    t,
    thirdPartyId,
  ]);

  return {
    restorableContacts,
    handleRestoreAccess,
  };
};
